import React from "react";
import { Button, Typography } from "antd";
import {
  BellOutlined,
  CheckOutlined,
  SettingOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

const { Title, Text } = Typography;

interface NotificationHeaderProps {
  unreadCount: number;
  isLoading: boolean;
  isMarkingAllAsRead: boolean;
  filter: "unread" | "read";
  readCount: number;
  onRefresh: () => void;
  onOpenSettings: () => void;
  onMarkAllAsRead: () => void;
  onFilterChange: (filter: "unread" | "read") => void;
}

const NotificationHeader: React.FC<NotificationHeaderProps> = ({
  unreadCount,
  isLoading,
  isMarkingAllAsRead,
  filter,
  readCount,
  onRefresh,
  onOpenSettings,
  onMarkAllAsRead,
  onFilterChange,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-4 sm:mb-6">
      {/* Header content - responsive layout */}
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div className="flex items-center space-x-3">
          <BellOutlined className="text-xl sm:text-2xl text-blue-500" />
          <div>
            <Title level={2} className="mb-0 text-lg sm:text-xl">
              Notifications
            </Title>
            <Text type="secondary" className="text-sm">
              {unreadCount > 0
                ? `${unreadCount} unread notification${
                    unreadCount !== 1 ? "s" : ""
                  }`
                : "All notifications read"}
            </Text>
          </div>
        </div>

        {/* Action buttons - responsive */}
        <div className="flex flex-wrap gap-2">
          <Button
            icon={<ReloadOutlined />}
            onClick={onRefresh}
            loading={isLoading}
            size="small"
            className="sm:size-default"
          >
            <span className="hidden sm:inline">Refresh</span>
          </Button>

          <Button
            icon={<SettingOutlined />}
            onClick={onOpenSettings}
            size="small"
            className="sm:size-default"
          >
            <span className="hidden sm:inline">Settings</span>
          </Button>

          {unreadCount > 0 && (
            <Button
              type="primary"
              icon={<CheckOutlined />}
              loading={isMarkingAllAsRead}
              onClick={onMarkAllAsRead}
              size="small"
              className="sm:size-default"
            >
              <span className="hidden sm:inline">
                Mark All Read ({unreadCount})
              </span>
              <span className="sm:hidden">Read All</span>
            </Button>
          )}
        </div>
      </div>

      {/* Filter Tabs - responsive */}
      <div className="flex flex-wrap gap-1 mt-4">
        {[
          { key: "unread", label: "Unread", count: unreadCount },
          { key: "read", label: "Read", count: readCount },
        ].map((tab) => (
          <Button
            key={tab.key}
            type={filter === tab.key ? "primary" : "default"}
            onClick={() => onFilterChange(tab.key as any)}
            size="small"
            className="flex-shrink-0 sm:mr-2"
          >
            <span className="hidden sm:inline">
              {tab.label} ({tab.count})
            </span>
            <span className="sm:hidden">{tab.label}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export default NotificationHeader;
